import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Container, Grid, Paper, Button, TextField, Alert, Snackbar } from '@mui/material';
import { LocationOn, Email, Phone } from '@mui/icons-material';
import { motion } from 'framer-motion';

const contactCards = [
  {
    icon: <LocationOn sx={{ fontSize: 36, color: 'primary.main' }} />, label: 'Location', value: '6WA,507, 5Th Floor, Dubai Airport Freezone, Dubai - UAE',
  },
  {
    icon: <Email sx={{ fontSize: 36, color: 'primary.main' }} />, label: 'Email', value: 'digiwooddubai@gmail.com',
  },
  {
    icon: <Phone sx={{ fontSize: 36, color: 'primary.main' }} />, label: 'Phone', value: '+971 547452077',
  },
];

const heroVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.1, ease: [0.23, 1.01, 0.32, 1] } },
};
const cardGridVariants = {
  visible: { transition: { staggerChildren: 0.13, delayChildren: 0.2 } },
};
const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, type: 'spring', stiffness: 70 } },
};
const formVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.23, 1.01, 0.32, 1] } },
};

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);
  
  const formRef = useRef(null);
  const [form, setForm] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    subject: '', 
    message: '' 
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleScrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create email content
    const emailSubject = `New Inquiry from ${form.name} - ${form.subject}`;
    const emailBody = `
Name: ${form.name}
Email: ${form.email}
Phone: ${form.phone}
Subject: ${form.subject}

Message:
${form.message}
    `;
    
    // Create mailto link
    const mailtoLink = `mailto:digiwooddubai@gmail.com?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Show success message
    setSnackbar({
      open: true,
      message: 'Email client opened! Please send the email to complete your inquiry.',
      severity: 'success'
    });
    
    // Reset form
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  return (
    <Box>
      {/* Hero Section */}
      <Box
        component={motion.section}
        initial="hidden"
        animate="visible"
        variants={heroVariants}
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          background: `url('/banner/banner4.jpg') center/cover no-repeat`,
          '&:before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(2.5px)',
            zIndex: 1,
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 2, textAlign: 'center', color: '#fff' }} maxWidth="sm">
          <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: '0.08em', textShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
            Let’s Start a Conversation
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 400, mb: 4, textShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
            Tell us what you’re looking for. We’ll reach back fast.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2, fontSize: '1.15rem', bgcolor: '#A67C52', '&:hover': { bgcolor: '#8B5E3C' } }}
            onClick={handleScrollToForm}
          >
            Get in Touch
          </Button>
        </Container>
      </Box>

      {/* Contact Cards Section */}
      <Container sx={{ py: { xs: 6, md: 8 } }}>
        <Grid
          container
          spacing={4}
          justifyContent="center"
          component={motion.div}
          variants={cardGridVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {contactCards.map((card, idx) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={card.label}
              component={motion.div}
              variants={cardVariants}
            >
              <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 4, minHeight: 150 }}>
                {card.icon}
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5, color: 'primary.main', letterSpacing: '0.04em' }}>{card.label}</Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '1.1rem' }}>{card.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact Form & Map Section - SEPARATE BLOCKS */}
      <Box sx={{ width: '100vw', maxWidth: '100%', px: { xs: 0, md: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {/* Map Block */}
        <Box sx={{ width: '100%', maxWidth: 700, mb: { xs: 4, md: 6 } }}>
          <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)', width: '100%', minWidth: { xs: 'unset', md: 420 } }}>
            <iframe
              title="DigiWood Dubai Location"
              src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d7216.589319499656!2d55.363711741969944!3d25.260671410680278!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1s6WA%20507%2C%205th%20FLOOR%20%20DUBAI%20AIRPORT%20FREEZONE%20(DAFZA)%20DUBAI%2C%20UNITED%20ARAB%20EMIRATES!5e0!3m2!1sen!2sin!4v1753977030701!5m2!1sen!2sin"
              width="100%"
              height="280"
              style={{ border: 0, display: 'block' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </Paper>
        </Box>
        {/* Form Block */}
        <Box sx={{ width: '100vw', px: 0, mx: 0 }} ref={formRef}>
          <Paper elevation={4} sx={{ p: { xs: 2, md: 4 }, borderRadius: 0, boxShadow: '0 4px 24px 0 rgba(166,124,82,0.10)', width: '100vw', minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, textAlign: 'center', color: 'primary.main', letterSpacing: '0.04em', fontSize: { xs: '1.5rem', md: '2.2rem' } }}>
              Send Us an Inquiry
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary', fontSize: { xs: '0.95rem', md: '1.1rem' } }}>
              Fill out the form below and we'll get back to you promptly
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
              {/* First row - All four fields using flexbox */}
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 2, width: '100%' }}>
                <Box sx={{ flex: '1 1 25%', minWidth: '150px' }}>
                  <TextField fullWidth label="Full Name *" name="name" value={form.name} onChange={handleChange} required variant="outlined" size="medium" sx={{'& .MuiOutlinedInput-root': {borderRadius: 2,'&:hover .MuiOutlinedInput-notchedOutline': {borderColor: '#A67C52',},},'& .Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: '#A67C52',}}} />
                </Box>
                <Box sx={{ flex: '1 1 25%', minWidth: '150px' }}>
                  <TextField fullWidth label="Email Address *" name="email" type="email" value={form.email} onChange={handleChange} required variant="outlined" size="medium" sx={{'& .MuiOutlinedInput-root': {borderRadius: 2,'&:hover .MuiOutlinedInput-notchedOutline': {borderColor: '#A67C52',},},'& .Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: '#A67C52',}}} />
                </Box>
                <Box sx={{ flex: '1 1 25%', minWidth: '150px' }}>
                  <TextField fullWidth label="Phone Number *" name="phone" value={form.phone} onChange={handleChange} required variant="outlined" size="medium" sx={{'& .MuiOutlinedInput-root': {borderRadius: 2,'&:hover .MuiOutlinedInput-notchedOutline': {borderColor: '#A67C52',},},'& .Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: '#A67C52',}}} />
                </Box>
                <Box sx={{ flex: '1 1 25%', minWidth: '150px' }}>
                  <TextField fullWidth label="Subject *" name="subject" value={form.subject} onChange={handleChange} required variant="outlined" size="medium" placeholder="e.g., Timber Inquiry" sx={{'& .MuiOutlinedInput-root': {borderRadius: 2,'&:hover .MuiOutlinedInput-notchedOutline': {borderColor: '#A67C52',},},'& .Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: '#A67C52',}}} />
                </Box>
              </Box>
              {/* Second row - Message field full width */}
              <TextField 
                fullWidth 
                label="Message *" 
                name="message" 
                value={form.message} 
                onChange={handleChange} 
                required 
                multiline 
                rows={6} 
                variant="outlined" 
                size="medium" 
                placeholder="Please describe your inquiry in detail..." 
                sx={{
                  width: '100% !important',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    width: '100% !important',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#A67C52',
                    },
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#A67C52',
                  }
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Button type="submit" variant="contained" color="primary" size="large" sx={{ px: 6, py: 1.5, fontWeight: 700, borderRadius: 3, fontSize: { xs: '1.1rem', md: '1.3rem' }, bgcolor: '#A67C52', '&:hover': { bgcolor: '#8B5E3C', transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(166,124,82,0.3)' }, transition: 'all 0.3s ease' }}>Send Inquiry</Button>
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', fontSize: { xs: '0.9rem', md: '1rem' }, maxWidth: 400 }}>
                  Clicking "Send Inquiry" will open your email app with a pre-filled message to <strong style={{ color: '#A67C52' }}>digiwooddubai@gmail.com</strong>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage; 